
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Trash2, Plus, RotateCcw, Search, RefreshCw, Users, Clock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useCareSpecialists } from '@/hooks/ca/useCareSpecialists.ts';
import { useCareSpecialistStatus } from '@/hooks/cs/useCareSpecialistStatus.ts';
import CareSpecialistDialog from '@/components/ca/CareSpecialistDialog.tsx';
import DeleteCareSpecialistDialog from '@/components/ca/DeleteCareSpecialistDialog.tsx';
import GlobalSearch from '@/components/features/search/GlobalSearch';
import LogoutConfirmDialog from '@/components/common/LogoutConfirmDialog.tsx';
import { useToast } from '@/hooks/use-toast';
import { useBackButtonLogout } from '@/hooks/ea/useBackButtonLogout.ts';
import NotificationPermissionBanner from '@/components/common/NotificationPermissionBanner';
import PaginationComponent from '@/components/common/PaginationComponent';
import CASearchForm from '@/components/ca/CASearchForm';
import UnifiedHeader from '@/components/common/UnifiedHeader';

const CADashboard: React.FC = () => {
  const { user, token, logout, isLoading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTransferPatients, setShowTransferPatients] = useState(false);
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [selectedEnroller, setSelectedEnroller] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [careSpecialistToDelete, setCareSpecialistToDelete] = useState<any>(null);

  // Search state management
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Check authentication on component mount
  useEffect(() => {
    if (!authLoading && (!user || !token)) {
      navigate('/', { replace: true });
      return;
    }

    // Check if user has CA or CS role
    if (user && !['CA', 'CS'].includes(user.type)) {
      navigate('/', { replace: true });
      return;
    }
  }, [user, token, authLoading, navigate]);

  // Handle browser back button to show logout dialog
  useBackButtonLogout({
    onLogoutRequest: () => setShowLogoutDialog(true)
  });


  // Use the useCareSpecialists hook
  const { careSpecialists, isLoading, error, refetch } = useCareSpecialists();

  // Use the care specialist status hook
  const { deferredTask, followUp, isLoading: statusLoading } = useCareSpecialistStatus();

  const handleDelete = (careSpecialist: any) => {
    setCareSpecialistToDelete(careSpecialist);
    setShowDeleteDialog(true);
  };

  const handleDeleteSuccess = () => {
    setCareSpecialistToDelete(null);
    refetch();
  };

  const handleEdit = (specialist: any) => {
    setSelectedEnroller(specialist);
    setShowEditDialog(true);
  };

  const handleLogout = () => {
    logout();
    setShowLogoutDialog(false);
    toast({
      title: "Signed Out",
      description: "You have been successfully signed out.",
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast({
        title: "Refreshed",
        description: "Dashboard data has been refreshed.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to refresh dashboard data",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSearchPatientsClick = () => {
    navigate('/global-search'); // Use React Router navigation instead of state
  };

  const handleBackToDashboard = () => {
    setShowGlobalSearch(false);
  };

  const handleEditSave = () => {
    refetch();
  };

  // Search handlers
  const handleSearch = () => {
    // Search is already happening automatically via real-time filtering
    // This function is just for aesthetics when the search button is clicked
  };

  const handleSearchReload = () => {
    setSearchTerm('');
    setIsSearching(false);
    refetch();
  };

  // Filter care specialists based on search term (real-time filtering)
  const filteredCareSpecialists = careSpecialists.filter((specialist) => {
    if (!searchTerm.trim()) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      specialist.full_name?.toLowerCase().includes(searchLower) ||
      specialist.email?.toLowerCase().includes(searchLower)
    );
  });

  // Calculate pagination using filtered data
  const totalPages = Math.ceil(filteredCareSpecialists.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = filteredCareSpecialists.slice(startIndex, endIndex);

  // Reset to first page when search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1);
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ca-dashboard-auth-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" data-testid="ca-dashboard-auth-spinner"></div>
      </div>
    );
  }

  // Don't render if user is not authenticated or doesn't have CA/CS role
  if (!user || !token || !['CA', 'CS'].includes(user.type)) {
    return null;
  }


  if (showGlobalSearch) {
    return <GlobalSearch onBack={handleBackToDashboard} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="ca-dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" data-testid="ca-dashboard-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8" data-testid="ca-dashboard-error">
        <p className="text-red-500" data-testid="ca-dashboard-error-message">Error loading patient enrollers. Please try again.</p>
        <Button onClick={() => refetch()} className="mt-4" data-testid="ca-dashboard-retry-button">
          <RotateCcw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

   return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50" data-testid="ca-dashboard">
      {/* Notification Permission Banner */}
      <NotificationPermissionBanner data-testid="ca-notification-banner" />

      {/* Search Bar Section */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-6 lg:px-8 py-4" data-testid="ca-search-section">
        <div className="max-w-7xl mx-auto">
          <CASearchForm
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            onSearch={handleSearch}
            onReload={handleSearchReload}
            isLoading={isSearching}
            data-testid="ca-search-form"
          />
        </div>
      </div>

      {/* Content Section */}
      <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="ca-dashboard-content">
        <div className="space-y-6">
          {/* Patient Categories Cards - Only show if there are follow-up or deferred tasks */}
          {(followUp || deferredTask) && (
            <div className={`grid gap-6 mb-8 ${followUp && deferredTask ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'
              }`} data-testid="ca-dashboard-cards-section">
              {/* Follow up Patients - Only show if followUp is true */}
              {followUp && (
                <Card className="bg-gradient-to-br from-orange-500 to-yellow-500 text-white hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105" data-testid="ca-dashboard-followup-card">
                  <CardContent className="p-6" data-testid="ca-dashboard-followup-content">
                    <div className="flex items-center justify-between" data-testid="ca-dashboard-followup-inner">
                      <div data-testid="ca-dashboard-followup-text">
                        <h3 className="text-lg font-semibold mb-2" data-testid="ca-dashboard-followup-title">Follow up Patients</h3>
                        <p className="text-orange-100 text-sm" data-testid="ca-dashboard-followup-description">Active follow-up tasks available</p>
                      </div>
                      <Clock className="h-8 w-8 text-orange-100" data-testid="ca-dashboard-followup-icon" />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Deferred Patients - Only show if deferredTask is true */}
              {deferredTask && (
                <Card className="bg-gradient-to-br from-red-500 to-pink-500 text-white hover:shadow-lg transition-all duration-300 cursor-pointer transform hover:scale-105" data-testid="ca-dashboard-deferred-card">
                  <CardContent className="p-6" data-testid="ca-dashboard-deferred-content">
                    <div className="flex items-center justify-between" data-testid="ca-dashboard-deferred-inner">
                      <div data-testid="ca-dashboard-deferred-text">
                        <h3 className="text-lg font-semibold mb-2" data-testid="ca-dashboard-deferred-title">Deferred Patients</h3>
                        <p className="text-red-100 text-sm" data-testid="ca-dashboard-deferred-description">Active deferred tasks available</p>
                      </div>
                      <AlertCircle className="h-8 w-8 text-red-100" data-testid="ca-dashboard-deferred-icon" />
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {filteredCareSpecialists.length === 0 ? (
            <div className="text-center py-12" data-testid="ca-dashboard-empty-state">
              <p className="text-gray-500 text-lg" data-testid="ca-dashboard-empty-message">
                {searchTerm ? `No care specialists found matching "${searchTerm}"` : 'No care specialists found'}
              </p>
            </div>
          ) : (
            <>
               {/* Table */}
              <Card className="bg-white shadow-sm mx-0" data-testid="ca-specialists-table-card">
                <CardContent className="p-0" data-testid="ca-specialists-table-content">
                  <div className="overflow-x-auto" data-testid="ca-specialists-table-wrapper">
                    <Table data-testid="ca-specialists-table">
                    <TableHeader data-testid="ca-specialists-table-header">
                      <TableRow data-testid="ca-specialists-header-row">
                        <TableHead data-testid="ca-table-header-name">Full Name</TableHead>
                        <TableHead data-testid="ca-table-header-email">Email</TableHead>
                        <TableHead data-testid="ca-table-header-clinics">Clinic Names</TableHead>
                        <TableHead data-testid="ca-table-header-tasks">Assigned Task(s)</TableHead>
                        <TableHead data-testid="ca-table-header-actions">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="ca-specialists-table-body">
                      {currentRecords.map((specialist) => (
                        <TableRow key={specialist.care_specialist_id} data-testid={`ca-specialist-row-${specialist.care_specialist_id}`}>
                          <TableCell className="font-medium" data-testid={`ca-specialist-name-${specialist.care_specialist_id}`}>{specialist.full_name}</TableCell>
                          <TableCell data-testid={`ca-specialist-email-${specialist.care_specialist_id}`}>{specialist.email}</TableCell>
                          <TableCell data-testid={`ca-specialist-clinics-${specialist.care_specialist_id}`}>
                            <div className="flex flex-wrap gap-1" data-testid={`ca-specialist-clinics-list-${specialist.care_specialist_id}`}>
                              {specialist.clinic_names && specialist.clinic_names !== 'No Clinics Assigned' ? (() => {
                                const clinicArray = specialist.clinic_names.split(', ');
                                const displayClinics = clinicArray.slice(0, 4);
                                const remainingCount = clinicArray.length - 4;

                                return (
                                  <>
                                    {displayClinics.map((clinic, index) => (
                                      <Badge key={index} variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid={`ca-specialist-clinic-badge-${specialist.care_specialist_id}-${index}`}>
                                        {clinic.trim()}
                                      </Badge>
                                    ))}
                                    {remainingCount > 0 && (
                                      <Badge
                                        variant="outline"
                                        className="text-xs bg-gray-50 text-gray-700 border-gray-200 cursor-pointer hover:bg-gray-100"
                                        title={`Click to see all ${clinicArray.length} clinics`}
                                        data-testid={`ca-specialist-clinic-remaining-${specialist.care_specialist_id}`}
                                      >
                                        +{remainingCount}
                                      </Badge>
                                    )}
                                  </>
                                );
                              })() :
                                <Badge variant="secondary" className="text-xs" data-testid={`ca-specialist-no-clinics-${specialist.care_specialist_id}`}>No Clinics Assigned</Badge>
                              }
                            </div>
                           </TableCell>
                          <TableCell data-testid={`ca-specialist-tasks-${specialist.care_specialist_id}`}>
                            {specialist.deferred_task || specialist.follow_up ? (
                              <div className="flex items-center gap-1 flex-nowrap" data-testid={`ca-specialist-tasks-list-${specialist.care_specialist_id}`}>
                                {specialist.deferred_task && <Badge variant="secondary" className="text-xs whitespace-nowrap" data-testid={`ca-specialist-deferred-badge-${specialist.care_specialist_id}`}>Deferred</Badge>}
                                {specialist.follow_up && <Badge variant="default" className="text-xs whitespace-nowrap" data-testid={`ca-specialist-followup-badge-${specialist.care_specialist_id}`}>Follow up</Badge>}
                              </div>
                            ) : (
                              <Badge variant="secondary" className="text-xs" data-testid={`ca-specialist-no-task-badge-${specialist.care_specialist_id}`}>No Task Assigned</Badge>
                            )}
                          </TableCell>
                          <TableCell data-testid={`ca-specialist-actions-${specialist.care_specialist_id}`}>
                            <div className="flex space-x-2" data-testid={`ca-specialist-actions-wrapper-${specialist.care_specialist_id}`}>
                              <Button
                                variant="outline"
                                size="icon"
                                aria-label={`Edit specialist ${specialist.full_name}`}
                                onClick={() => handleEdit(specialist)}
                                className="h-8 w-8"
                                data-testid={`ca-specialist-edit-button-${specialist.care_specialist_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                aria-label={`Delete specialist ${specialist.full_name}`}
                                size="icon"
                                onClick={() => handleDelete(specialist)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                                data-testid={`ca-specialist-delete-button-${specialist.care_specialist_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              {/* Common Pagination Component */}
              <div className="pagination-container" data-testid="ca-specialists-pagination-container">
                <PaginationComponent
                  currentPage={currentPage}
                  totalPages={totalPages}
                  recordsPerPage={recordsPerPage}
                  totalRecords={filteredCareSpecialists.length}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  onPageChange={handlePageChange}
                  onRecordsPerPageChange={handleRecordsPerPageChange}
                  data-testid="ca-specialists-pagination"
                />
              </div>
            </>
          )}

          {/* Dialogs */}
          <CareSpecialistDialog
            open={showAddDialog}
            onOpenChange={setShowAddDialog}
            onSuccess={() => {
              refetch();
            }}
            data-testid="ca-add-specialist-dialog"
          />

          <DeleteCareSpecialistDialog
            open={showDeleteDialog}
            onOpenChange={setShowDeleteDialog}
            careSpecialist={careSpecialistToDelete}
            onSuccess={handleDeleteSuccess}
            data-testid="ca-delete-specialist-dialog"
          />

          <CareSpecialistDialog
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            onSuccess={() => {
              refetch();
              setShowEditDialog(false);
              setSelectedEnroller(null);
            }}
            careSpecialist={selectedEnroller}
            isEditMode={true}
            data-testid="ca-edit-specialist-dialog"
          />

          <LogoutConfirmDialog
            open={showLogoutDialog}
            onOpenChange={setShowLogoutDialog}
            onConfirm={handleLogout}
            data-testid="ca-logout-confirm-dialog"
          />
        </div>
      </main>
    </div>
  );
};

export default CADashboard;
